import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';
import WhyOmanSectorDeck from './WhyOmanSectorDeck';

const SECTOR_TRANSLATIONS = {
  names: {
    fisheries: 'مصايد الأسماك',
    fish: 'مصايد الأسماك',
    'agri-food': 'الأغذية الزراعية',
    'agri-food & fisheries': 'الأغذية الزراعية ومصايد الأسماك',
    logistics: 'اللوجستيات',
    manufacturing: 'التصنيع',
    mining: 'التعدين',
    renewables: 'الطاقة المتجددة',
    'renewable energy': 'الطاقة المتجددة',
    tourism: 'السياحة',
    agriculture: 'الزراعة',
    energy: 'الطاقة',
    technology: 'التكنولوجيا',
    healthcare: 'الرعاية الصحية',
    education: 'التعليم',
    'real estate': 'العقارات',
    finance: 'المالية',
    'oil & gas': 'النفط والغاز',
    ict: 'تقنية المعلومات',
  },
  descriptions: {
    "Oman's fisheries exports reached US$489M in 2023, with strong sustainability credentials. Agri-food processing offers growing opportunities.":
      'وصلت صادرات مصايد الأسماك في عُمان إلى 489 مليون دولار في 2023، مع معايير استدامة قوية. يوفر قطاع تصنيع الأغذية الزراعية فرصاً متنامية.',
    "Oman's strategic location makes it a prime logistics hub connecting Asia, Africa, and Europe through world-class ports and infrastructure.":
      'يجعل موقع عُمان الاستراتيجي منها مركزاً لوجستياً رئيسياً يربط آسيا وأفريقيا وأوروبا عبر موانئ وبنية تحتية عالمية المستوى.',
    'Growing manufacturing sector with attractive incentives and access to regional markets.':
      'قطاع تصنيعي متنامٍ مع حوافز جذابة وإمكانية الوصول إلى الأسواق الإقليمية.',
    'Rich mineral resources with exploration and extraction opportunities across the sultanate.':
      'موارد معدنية غنية مع فرص استكشاف واستخراج في جميع أنحاء السلطنة.',
    'Renewable energy and green hydrogen sector with ambitious national targets and incentives.':
      'قطاع الطاقة المتجددة والهيدروجين الأخضر مع أهداف وطنية طموحة وحوافز.',
    'Unique tourism destination with rich cultural heritage, stunning landscapes, and luxury resorts.':
      'وجهة سياحية فريدة بتراث ثقافي غني ومناظر طبيعية خلابة ومنتجعات فاخرة.',
    "Sustainable fishing industry with extensive investment opportunities in Oman's rich coastal waters.":
      'صناعة صيد مستدامة مع فرص استثمارية واسعة في المياه الساحلية الغنية لعُمان.',
    'Advanced logistics infrastructure connecting global markets through strategic ports and free zones.':
      'بنية تحتية لوجستية متطورة تربط الأسواق العالمية عبر موانئ استراتيجية ومناطق حرة.',
  },
};

const translateSector = (sector, language) => {
  if (language !== 'ar') return sector;

  const nameLower = sector.name?.toLowerCase() || '';
  let arabicName = sector.name;
  for (const [key, value] of Object.entries(SECTOR_TRANSLATIONS.names)) {
    if (nameLower.includes(key)) {
      arabicName = value;
      break;
    }
  }

  let arabicDescription = sector.description;
  for (const [key, value] of Object.entries(SECTOR_TRANSLATIONS.descriptions)) {
    if (sector.description?.toLowerCase().includes(key.toLowerCase().slice(0, 30))) {
      arabicDescription = value;
      break;
    }
  }
  if (SECTOR_TRANSLATIONS.descriptions[sector.description]) {
    arabicDescription = SECTOR_TRANSLATIONS.descriptions[sector.description];
  }

  return { ...sector, name: arabicName, description: arabicDescription };
};

const WhyOman = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const { t, isRTL, language } = useLanguage();

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getSectors();
        if (data && data.length > 0) {
          setSectors(data);
        } else {
          setError('No sectors data found');
          setSectors([]);
        }
      } catch (err) {
        console.error('Error fetching sectors:', err);
        setError(err.message);
        setSectors([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSectors();
  }, []);

  const fallbackFeatures = useMemo(
    () => [
      {
        name: language === 'ar' ? 'مصايد الأسماك' : 'Fisheries',
        description:
          language === 'ar'
            ? 'قطاع صيد الأسماك المستدام مع فرص استثمارية واسعة.'
            : "Sustainable fishing industry with extensive investment opportunities in Oman's rich coastal waters.",
      },
      {
        name: language === 'ar' ? 'اللوجستيات' : 'Logistics',
        description:
          language === 'ar'
            ? 'بنية تحتية لوجستية متطورة تربط الأسواق العالمية.'
            : 'Advanced logistics infrastructure connecting global markets through strategic ports and free zones.',
      },
      {
        name: language === 'ar' ? 'التصنيع' : 'Manufacturing',
        description:
          language === 'ar'
            ? 'قطاع تصنيعي متنامي مع حوافز استثمارية جذابة.'
            : 'Growing manufacturing sector with attractive incentives and access to regional markets.',
      },
      {
        name: language === 'ar' ? 'التعدين' : 'Mining',
        description:
          language === 'ar'
            ? 'موارد معدنية غنية مع فرص استكشاف واستخراج.'
            : 'Rich mineral resources with exploration and extraction opportunities across the sultanate.',
      },
      {
        name: language === 'ar' ? 'الطاقة المتجددة' : 'Renewable Energy',
        description:
          language === 'ar'
            ? 'قطاع الطاقة المتجددة والهيدروجين الأخضر.'
            : 'Renewable energy and green hydrogen sector with ambitious national targets and incentives.',
      },
      {
        name: language === 'ar' ? 'السياحة' : 'Tourism',
        description:
          language === 'ar'
            ? 'وجهة سياحية فريدة بتراث ثقافي غني.'
            : 'Unique tourism destination with rich cultural heritage, stunning landscapes, and luxury resorts.',
      },
    ],
    [language]
  );

  const translatedSectors = useMemo(
    () => sectors.map((sector) => translateSector(sector, language)),
    [sectors, language]
  );

  const displayData = translatedSectors.length > 0 ? translatedSectors : fallbackFeatures;

  useEffect(() => {
    if (activeSlide >= displayData.length) {
      setActiveSlide(0);
    }
  }, [displayData.length, activeSlide]);

  return (
    <section id="why-oman" className="relative overflow-hidden bg-[#F9F9FB]" dir={isRTL ? 'rtl' : 'ltr'}>
      <div
        className="mx-auto w-full max-w-[1344px] px-6 sm:px-10 lg:px-[140px] py-[50px] lg:py-[70px]"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        <div className="flex flex-col gap-10 lg:gap-[60px]">
          <div className="flex w-full flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 max-w-[749px] flex-col gap-6 text-start lg:gap-[31px]">
              <h2 className="text-[#51516B] text-[28px] sm:text-[32px] lg:text-[36px] font-bold leading-[1.2] lg:leading-[46px]">
                {t('website.whyOman.keySectorsTitle')}
              </h2>
              <p className="text-[#51516B] text-base font-normal leading-6">
                {t('website.whyOman.keySectorsDescription')}
              </p>
            </div>
            <a
              href="https://investoman.om/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-[54px] shrink-0 items-center justify-center gap-1.5 self-start rounded-full px-[18px] text-base font-semibold text-white shadow-sm transition hover:opacity-95"
              style={{
                background: 'linear-gradient(90deg, rgba(84, 187, 222, 0.85) 0%, rgba(177, 220, 213, 0.85) 100%)',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
              }}
            >
              {t('website.whyOman.exploreAllSectors')}
              <svg width="7" height="12" viewBox="0 0 10 14" fill="none" aria-hidden className="ms-0.5">
                <path
                  d="M1.5 1.5L7.5 7L1.5 12.5"
                  stroke="white"
                  strokeWidth="1.29"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#54BBDE]/25">
                <svg className="h-7 w-7 animate-spin text-[#492E8B]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <p className="text-[#51516B]">{t('website.whyOman.loading')}</p>
            </div>
          )}

          {!loading && displayData.length > 0 && (
            <WhyOmanSectorDeck sectors={displayData} activeIndex={activeSlide} setActiveIndex={setActiveSlide} />
          )}

          {!loading && !error && sectors.length === 0 && displayData.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm">
              <p className="text-gray-500">{t('website.whyOman.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhyOman;
